/**
 * Account auth API client (Final Phase Stage B). Typed fetch wrappers over the backend
 * /api/v1/auth endpoints: challenge → verify (Sui/EVM/Solana) → JWT session.
 * See requirements/sui-dependency-reduction-final-phase-logins-and-payments.md.
 */
import { API_BASE_URL } from '../config/apiConfig';

const AUTH_BASE = `${API_BASE_URL}/api/v1/auth`;

export type AuthChain = 'sui' | 'evm' | 'solana';
export interface WalletProof {
  chain: AuthChain;
  wallet: string;
  nonce: string;
  signature: string;
  /**
   * Optional default trainer name from a social login's username. The backend seeds it
   * only if the account has no name yet (never overwrites an edited name) and re-sanitizes
   * it server-side. Omitted for wallet logins with no embedded social profile.
   */
  displayName?: string;
}
export interface AuthSession {
  account_id: string;
  token: string;
  /**
   * True only on the login that created the account (brand-new wallet). Drives the one-time
   * new-wallet activation flow. Optional → back-compatible with older backend responses.
   */
  is_new?: boolean;
}

async function postJson<T>(path: string, body: unknown, token?: string): Promise<T> {
  const res = await fetch(`${AUTH_BASE}${path}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(30_000),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw Object.assign(new Error((data as { error?: string }).error ?? 'Auth request failed'), {
      status: res.status,
    });
  }
  return data as T;
}

export function getAuthChallenge(): Promise<{ nonce: string }> {
  return postJson('/challenge', {});
}

export function verifyWallet(proof: WalletProof): Promise<AuthSession> {
  return postJson<AuthSession>('/verify', proof);
}

/** Exchange a Supabase Auth access token (email / OAuth login) for our account session. */
export function exchangeSupabaseSession(accessToken: string): Promise<AuthSession> {
  return postJson<AuthSession>('/supabase', { accessToken });
}

export async function linkWalletApi(
  token: string,
  proof: WalletProof
): Promise<{ ok: boolean; conflict?: boolean }> {
  try {
    return await postJson<{ ok: boolean }>('/link-wallet', proof, token);
  } catch (e) {
    if ((e as { status?: number }).status === 409) return { ok: false, conflict: true };
    throw e;
  }
}

/** Exchange a CrazyGames user token (Login with CrazyGames, inside their frame) for our account session. */
export function exchangeCrazyGamesToken(token: string): Promise<AuthSession> {
  return postJson<AuthSession>('/crazygames', { token });
}
